import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { visitLogsAPI, visitsAPI, clientsAPI, aiAPI } from '../services/api';
import CareNotesFeed from '../components/CareNotesFeed';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Plus, 
  Sparkle,
  Spinner
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const VisitLogsPage = () => {
  const { user } = useAuth();
  const [visits, setVisits] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [aiAssisting, setAiAssisting] = useState(false);
  const [formData, setFormData] = useState({
    visit_id: '',
    log_type: 'general',
    content: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [visitsRes, clientsRes] = await Promise.all([
        visitsAPI.getAll({}),
        clientsAPI.getAll()
      ]);
      setVisits(visitsRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      console.error('Error loading visit logs:', error);
      toast.error('Failed to load visit logs');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLog = async (e) => {
    e.preventDefault();
    try {
      await visitLogsAPI.create(formData);
      toast.success('Visit log created successfully');
      setIsCreateOpen(false);
      setFormData({
        visit_id: '',
        log_type: 'general',
        content: ''
      });
      setRefreshKey(k => k + 1);
    } catch (error) {
      toast.error('Failed to create visit log');
    }
  };

  const handleAIAssist = async () => {
    if (!formData.visit_id || !formData.content) {
      toast.error('Please select a visit and enter some notes first');
      return;
    }
    
    setAiAssisting(true);
    try {
      const response = await aiAPI.assistDocumentation(formData.visit_id, formData.content);
      setFormData(prev => ({
        ...prev,
        content: response.data.improved_notes
      }));
      toast.success('AI improved your notes');
    } catch (error) {
      console.error('AI assist error:', error);
      toast.error('Failed to get AI assistance');
    } finally {
      setAiAssisting(false);
    }
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.name || 'Unknown Client';
  };

  // Get visits that can have logs added (in_progress or completed)
  const loggableVisits = visits.filter(v => v.status === 'in_progress' || v.status === 'completed');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="visit-logs-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground">
            Care Notes
          </h1>
          <p className="text-muted-foreground mt-1">
            Visit notes by client — pick a day or week
          </p>
        </div>
        {(user?.role === 'caregiver' || user?.role === 'supervisor') && (
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-xl gap-2" data-testid="create-log-btn">
                <Plus weight="bold" className="w-4 h-4" />
                Add Log Entry
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading">Create Visit Log</DialogTitle>
                <DialogDescription>
                  Document activities, observations, or incidents
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateLog} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Visit *</Label>
                  <Select value={formData.visit_id} onValueChange={(value) => setFormData({...formData, visit_id: value})}>
                    <SelectTrigger className="rounded-xl" data-testid="log-visit-select">
                      <SelectValue placeholder="Select visit" />
                    </SelectTrigger>
                    <SelectContent>
                      {loggableVisits.map(visit => (
                        <SelectItem key={visit.id} value={visit.id}>
                          {getClientName(visit.client_id)} - {visit.scheduled_date} at {visit.scheduled_time}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Log Type *</Label>
                  <Select value={formData.log_type} onValueChange={(value) => setFormData({...formData, log_type: value})}>
                    <SelectTrigger className="rounded-xl" data-testid="log-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General Notes</SelectItem>
                      <SelectItem value="observation">Client Observation</SelectItem>
                      <SelectItem value="activity">Activity Completed</SelectItem>
                      <SelectItem value="incident">Incident Report</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Content *</Label>
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm"
                      className="rounded-xl gap-1 h-7 text-xs"
                      onClick={handleAIAssist}
                      disabled={aiAssisting || !formData.content}
                      data-testid="ai-assist-btn"
                    >
                      {aiAssisting ? (
                        <Spinner className="w-3 h-3 animate-spin" />
                      ) : (
                        <Sparkle weight="duotone" className="w-3 h-3" />
                      )}
                      AI Improve
                    </Button>
                  </div>
                  <Textarea
                    value={formData.content}
                    onChange={(e) => setFormData({...formData, content: e.target.value})}
                    placeholder="Describe the visit activities, client condition, observations..."
                    required
                    rows={6}
                    className="rounded-xl"
                    data-testid="log-content-input"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" className="flex-1 rounded-xl" onClick={() => setIsCreateOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1 rounded-xl" data-testid="create-log-submit">
                    Save Log
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Care notes feed: filter by client + day/week, grouped by day */}
      <CareNotesFeed clients={clients} refreshKey={refreshKey} />
    </div>
  );
};

export default VisitLogsPage;
